import { session } from './session';

/**
 * HTTP client for the CRM API (`/api/v1`).
 *  - Bearer access token from memory; `X-Client-App: web` on every call.
 *  - 401 → one single-flight refresh (`/identity/sessions/refresh`) shared by all callers, then the request is retried once.
 *    If refresh fails the session is cleared and `onUnauthenticated` fires (router sends the user to /login).
 *  - Error bodies `{ errorCode, detail, traceId, fieldErrors }` become `ApiError`.
 *  - Money/decimal values are strings both ways — never convert them to `number` here.
 */
export const API_BASE = '/api/v1';
export const CLIENT_APP = 'web';

export type FieldErrors = Record<string, string[]>;

export interface ErrorBody {
  type?: string;
  title?: string;
  status?: number;
  errorCode?: string;
  detail?: string;
  traceId?: string;
  fieldErrors?: FieldErrors | null;
  /** 409 TENANT_SELECTION_REQUIRED on login: the tenants the user may choose from. */
  tenants?: { code: string; name: string }[];
}

export class ApiError extends Error {
  readonly status: number;
  readonly errorCode: string;
  readonly detail: string;
  readonly traceId?: string;
  readonly fieldErrors: FieldErrors;
  /** The raw error envelope (endpoint-specific extras such as `tenants`). */
  readonly body: ErrorBody;

  constructor(status: number, body: ErrorBody) {
    const detail = body.detail || body.title || defaultMessage(status);
    super(detail);
    this.name = 'ApiError';
    this.status = status;
    this.errorCode = body.errorCode || (status === 0 ? 'NETWORK_ERROR' : `HTTP_${status}`);
    this.detail = detail;
    this.traceId = body.traceId;
    this.fieldErrors = body.fieldErrors ?? {};
    this.body = body;
  }

  get isNetwork() {
    return this.status === 0;
  }
  get isUnauthenticated() {
    return this.status === 401;
  }
  get isForbidden() {
    return this.status === 403 || this.errorCode === 'PERMISSION_DENIED';
  }
  get isNotFound() {
    return this.status === 404;
  }
  /** Optimistic-concurrency conflict — show ConflictBanner ("Dữ liệu đã thay đổi, tải lại"). */
  get isConflict() {
    return this.errorCode === 'VERSION_CONFLICT';
  }
  get isDuplicate() {
    return this.errorCode === 'DUPLICATE_ENTITY';
  }
  get isValidation() {
    return this.errorCode === 'VALIDATION_FAILED' || Object.keys(this.fieldErrors).length > 0;
  }
}

function defaultMessage(status: number) {
  if (status === 0) return 'Không kết nối được máy chủ. Kiểm tra mạng và thử lại.';
  if (status === 401) return 'Phiên đăng nhập đã hết hạn.';
  if (status === 403) return 'Không đủ quyền thực hiện thao tác.';
  if (status === 404) return 'Không tìm thấy dữ liệu.';
  if (status >= 500) return 'Lỗi hệ thống. Vui lòng thử lại hoặc liên hệ quản trị.';
  return 'Yêu cầu không thành công.';
}

/** New Idempotency-Key for commands with money/approval/creation effects. Generate once per user intent, reuse on retry. */
export function idempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  // RFC4122 v4 fallback (older browsers / non-secure contexts)
  const b = new Uint8Array(16);
  crypto.getRandomValues(b);
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

export interface SessionPayload {
  accessToken: string;
  accessTokenExpiresAtUtc: string;
  refreshToken: string;
  user: unknown;
}

interface HttpConfig {
  onUnauthenticated: () => void;
  onSessionRefreshed: (payload: SessionPayload) => void;
}

const config: HttpConfig = {
  onUnauthenticated: () => {},
  onSessionRefreshed: () => {},
};

export function configureHttp(next: Partial<HttpConfig>) {
  Object.assign(config, next);
}

export type QueryValue = string | number | boolean | null | undefined;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  query?: Record<string, QueryValue | QueryValue[]>;
  body?: unknown;
  /** `true` → generate a key; string → reuse (for retry of the same intent). */
  idempotencyKey?: string | true;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Skip the auth header + refresh logic (login/refresh endpoints). */
  anonymous?: boolean;
  /** Return the raw Response (downloads). */
  raw?: boolean;
}

export function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = path.startsWith('http') || path.startsWith('/api') ? path : `${API_BASE}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    const values = Array.isArray(v) ? v : [v];
    for (const item of values) {
      if (item === undefined || item === null || item === '') continue;
      params.append(k, String(item));
    }
  }
  const qs = params.toString();
  return qs ? `${url}${url.includes('?') ? '&' : '?'}${qs}` : url;
}

let refreshing: Promise<boolean> | null = null;

/** Single-flight refresh: concurrent 401s share one refresh call. Resolves false when the session is gone. */
export function refreshSession(): Promise<boolean> {
  if (refreshing) return refreshing;
  const refreshToken = session.getRefreshToken();
  if (!refreshToken) return Promise.resolve(false);
  refreshing = (async () => {
    try {
      const res = await fetch(buildUrl('/identity/sessions/refresh'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Client-App': CLIENT_APP },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) {
        // A network hiccup on refresh must not log the user out; only an explicit rejection does.
        if (res.status === 401 || res.status === 400 || res.status === 403) {
          session.clear();
          config.onUnauthenticated();
        }
        return false;
      }
      const payload = (await res.json()) as SessionPayload;
      session.setTokens(payload.accessToken, payload.accessTokenExpiresAtUtc, payload.refreshToken);
      config.onSessionRefreshed(payload);
      return true;
    } catch {
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

async function parseError(res: Response): Promise<ApiError> {
  let body: ErrorBody = {};
  try {
    const text = await res.text();
    if (text) body = JSON.parse(text) as ErrorBody;
  } catch {
    /* non-JSON error body */
  }
  return new ApiError(res.status, body);
}

function authHeaders(anonymous?: boolean): Record<string, string> {
  const h: Record<string, string> = { 'X-Client-App': CLIENT_APP, Accept: 'application/json' };
  const token = session.getAccessToken();
  if (!anonymous && token) h.Authorization = `Bearer ${token}`;
  return h;
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const url = buildUrl(path, opts.query);
  const isForm = typeof FormData !== 'undefined' && opts.body instanceof FormData;
  const idemKey = opts.idempotencyKey === true ? idempotencyKey() : opts.idempotencyKey;

  // Resume a session after reload: no access token yet but a refresh token exists.
  if (!opts.anonymous && !session.getAccessToken() && session.getRefreshToken()) await refreshSession();

  const send = () => {
    const headers: Record<string, string> = { ...authHeaders(opts.anonymous), ...opts.headers };
    if (opts.body !== undefined && !isForm) headers['Content-Type'] = 'application/json';
    if (idemKey) headers['Idempotency-Key'] = idemKey;
    return fetch(url, {
      method: opts.method ?? (opts.body !== undefined ? 'POST' : 'GET'),
      headers,
      body: opts.body === undefined ? undefined : isForm ? (opts.body as FormData) : JSON.stringify(opts.body),
      signal: opts.signal,
    });
  };

  let res: Response;
  try {
    res = await send();
    if (res.status === 401 && !opts.anonymous) {
      const ok = await refreshSession();
      if (ok) res = await send();
    }
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    throw new ApiError(0, {});
  }

  if (!res.ok) {
    const err = await parseError(res);
    if (res.status === 401 && !opts.anonymous) {
      session.clear();
      config.onUnauthenticated();
    }
    throw err;
  }
  if (opts.raw) return res as unknown as T;
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export const http = {
  get: <T>(path: string, query?: RequestOptions['query'], opts?: Omit<RequestOptions, 'query' | 'method'>) =>
    request<T>(path, { ...opts, query, method: 'GET' }),
  post: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'body' | 'method'>) =>
    request<T>(path, { ...opts, body: body ?? {}, method: 'POST' }),
  put: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'body' | 'method'>) =>
    request<T>(path, { ...opts, body: body ?? {}, method: 'PUT' }),
  patch: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'body' | 'method'>) =>
    request<T>(path, { ...opts, body: body ?? {}, method: 'PATCH' }),
  delete: <T>(path: string, opts?: Omit<RequestOptions, 'method'>) => request<T>(path, { ...opts, method: 'DELETE' }),
};

/** Downloads an authenticated file (documents need the Bearer header, so a plain link can't be used). */
export async function downloadFile(path: string, fallbackName = 'tai-lieu'): Promise<void> {
  const res = await request<Response>(path, { raw: true });
  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(disposition);
  const name = match ? decodeURIComponent(match[1] ?? match[2] ?? fallbackName) : fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export interface UploadOptions {
  onProgress?: (percent: number) => void;
  signal?: AbortSignal;
}

/** Multipart upload with progress (XHR — fetch has no upload progress). Same auth/refresh/error rules as `request`. */
export function upload<T>(path: string, form: FormData, opts: UploadOptions = {}): Promise<T> {
  const attempt = (): Promise<{ status: number; text: string }> =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', buildUrl(path));
      Object.entries(authHeaders()).forEach(([k, v]) => xhr.setRequestHeader(k, v));
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) opts.onProgress?.(Math.round((e.loaded / e.total) * 100));
      };
      xhr.onload = () => resolve({ status: xhr.status, text: xhr.responseText });
      xhr.onerror = () => reject(new ApiError(0, {}));
      xhr.onabort = () => reject(new DOMException('Aborted', 'AbortError'));
      opts.signal?.addEventListener('abort', () => xhr.abort());
      xhr.send(form);
    });

  return (async () => {
    if (!session.getAccessToken() && session.getRefreshToken()) await refreshSession();
    let res = await attempt();
    if (res.status === 401 && (await refreshSession())) res = await attempt();
    if (res.status < 200 || res.status >= 300) {
      let body: ErrorBody = {};
      try {
        body = JSON.parse(res.text) as ErrorBody;
      } catch {
        /* ignore */
      }
      throw new ApiError(res.status, body);
    }
    return (res.text ? JSON.parse(res.text) : undefined) as T;
  })();
}

/** Human-readable message for any thrown value. */
export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.detail;
  if (e instanceof Error) return e.message;
  return 'Đã có lỗi xảy ra.';
}
